import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useToast } from '../hooks/useToast'

interface Option {
  id: string
  label: string
  [key: string]: any
}

interface Props {
  options: Option[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  formatLabel?: (option: Option) => string
  allowCreate?: boolean
  onCreate?: (newValue: string) => Promise<{ id: string; label: string }>
  onCreateClick?: (searchTerm: string) => void | Promise<void>
  // When provided, the dropdown list is populated by server-side search instead of
  // filtering `options` client-side — needed for tables too large to load in full
  // (see ports: 41k+ rows, far past PostgREST's default page size).
  onSearch?: (term: string) => Promise<Option[]>
  // Explicit label for the current `value` when it may not appear in `options`/search
  // results (e.g. it's since been deactivated) — otherwise a stale/hidden selection
  // renders as blank even though `value` itself is still set correctly.
  selectedOption?: Option | null
  // Z41: shown instead of the generic "No matches found" when `options` itself is empty --
  // distinguishes "nothing on file to search" (a data-loading problem worth flagging) from
  // "nothing matched what you typed" (just keep typing/adjust the search).
  emptyStateHint?: string
  // Red border for a required-but-empty field, matching a plain <input>'s usual invalid styling
  // -- callers clear it themselves once the field is filled (see ChargesTab's fieldErrors).
  invalid?: boolean
  // Z20: BoxTop-style paired code+name lookup. When set, client-side filtering (the
  // `options` array path, not `onSearch`) matches codeKey by PREFIX and nameKey by
  // SUBSTRING instead of the default whole-label substring match -- e.g. typing "FXT"
  // matches the code but typing "XT" alone doesn't, while typing "FELIXSTOWE" still
  // matches on name. Also drives the default "CODE - NAME" display when formatLabel
  // isn't explicitly overridden. Server-side (onSearch) callers do their own matching
  // and only pick up the default display format from this.
  codeKey?: string
  nameKey?: string
  // Alternate code-like fields also matched by prefix (aliases[] arrays, or single
  // values like carriers.iata_prefix — e.g. typing "618" matches Singapore Airlines).
  aliasesKey?: string
  extraCodeKeys?: string[]
  // Z43: an ancestor with overflow-x-auto (e.g. a wide table wrapper) forces overflow-y to
  // 'auto' too and clips this dropdown's absolutely-positioned list -- looks exactly like
  // "zero options" even though `options` loaded fine. Renders the list into a portal on
  // document.body, positioned from the input's own bounding rect, so it escapes any clipping
  // ancestor. Opt-in (not the default) so this only changes behavior where it's asked for.
  usePortal?: boolean
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search and select...',
  disabled = false,
  formatLabel,
  allowCreate = true,
  onCreate,
  onCreateClick,
  onSearch,
  selectedOption: selectedOptionProp,
  codeKey,
  nameKey,
  aliasesKey,
  extraCodeKeys,
  emptyStateHint,
  usePortal = false,
  invalid = false
}: Props) {
  const resolvedFormatLabel = formatLabel ?? (codeKey && nameKey
    ? (opt: Option) => `${opt[codeKey] || '—'} - ${opt[nameKey]}`
    : (opt: Option) => opt.label)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isCreating, setIsCreating] = useState(false)
  const [remoteOptions, setRemoteOptions] = useState<Option[]>([])
  const [searching, setSearching] = useState(false)
  const [portalRect, setPortalRect] = useState<{ top: number; left: number; width: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const portalPanelRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()

  const selectedOption =
    selectedOptionProp ??
    options.find(opt => opt.id === value) ??
    remoteOptions.find(opt => opt.id === value)

  // Z88: with thousands of options (e.g. every customer) rendering them all freezes the page.
  // Cap what's shown; the user narrows by typing (matching still runs over the full list).
  const MAX_RENDERED = 200
  const allMatches = onSearch
    ? remoteOptions
    : options.filter(opt => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return true
        if (codeKey && nameKey) {
          const code = String(opt[codeKey] ?? '').toLowerCase()
          const name = String(opt[nameKey] ?? '').toLowerCase()
          if (code.startsWith(term) || name.includes(term)) return true
          const extraCodes = (extraCodeKeys ?? []).map(k => String(opt[k] ?? '').toLowerCase())
          if (extraCodes.some(c => c && c.startsWith(term))) return true
          const aliases: string[] = aliasesKey ? (opt[aliasesKey] ?? []) : []
          if (aliases.some(a => String(a).toLowerCase().startsWith(term))) return true
          return false
        }
        return resolvedFormatLabel(opt).toLowerCase().includes(term)
      })
  const filteredOptions = allMatches.length > MAX_RENDERED ? allMatches.slice(0, MAX_RENDERED) : allMatches
  const truncatedCount = allMatches.length - filteredOptions.length

  // Server-side search mode: re-query (debounced) whenever the dropdown is open and
  // the search term changes, instead of relying on a fully-loaded `options` list.
  useEffect(() => {
    if (!onSearch || !isOpen) return
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const results = await onSearch(searchTerm)
        if (!cancelled) setRemoteOptions(results)
      } catch (error) {
        console.error('[SearchableSelect] onSearch failed:', error)
        if (!cancelled) setRemoteOptions([])
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [onSearch, isOpen, searchTerm])

  const handleSelect = (option: Option) => {
    onChange(option.id)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  const handleCreate = async () => {
    if (!searchTerm.trim() || isCreating) {
      console.warn('[SearchableSelect] handleCreate called but conditions not met:', {
        searchTerm: searchTerm.trim(),
        isCreating
      })
      return
    }

    // If onCreateClick is provided, use that instead (for custom dialog handling)
    if (onCreateClick) {
      console.log('[SearchableSelect] Using onCreateClick for:', searchTerm.trim())
      setIsCreating(true)
      try {
        await onCreateClick(searchTerm.trim())
        // onCreateClick handler will manage closing and selection
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to create item'
        console.error('[SearchableSelect] Error in onCreateClick:', errorMsg, error)
        toast(errorMsg, 'error')
      } finally {
        setIsCreating(false)
      }
      return
    }

    // Otherwise use onCreate for direct creation
    if (!onCreate) {
      console.warn('[SearchableSelect] Neither onCreate nor onCreateClick provided')
      return
    }

    setIsCreating(true)
    try {
      console.log('[SearchableSelect] Creating new item:', searchTerm.trim())
      const newOption = await onCreate(searchTerm.trim())
      console.log('[SearchableSelect] Item created successfully:', newOption)
      onChange(newOption.id)
      toast(`Created and selected: ${newOption.label}`, 'success')
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to create item'
      console.error('[SearchableSelect] Error creating item:', errorMsg, error)
      toast(errorMsg, 'error')
    } finally {
      setIsCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleSelect(filteredOptions[highlightedIndex])
        } else if (
          filteredOptions.length === 0 &&
          allowCreate &&
          (onCreate || onCreateClick) &&
          searchTerm.trim()
        ) {
          handleCreate()
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
        break
      default:
        break
    }
  }

  // Close when clicking outside -- with usePortal, the option list itself lives outside
  // containerRef (it's appended to document.body), so it also needs checking or every option
  // click would be misread as an outside click and close the list before onClick can fire.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!portalPanelRef.current || !portalPanelRef.current.contains(target))
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Z43: when portaled, position the list from the input's live bounding rect instead of
  // relying on normal-flow CSS positioning (top-full etc.), since it's no longer a sibling of
  // the input in the DOM. Recomputed on open and kept in sync while open in case the page
  // scrolls or resizes underneath it.
  useEffect(() => {
    if (!usePortal || !isOpen) return
    const updateRect = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (rect) setPortalRect({ top: rect.bottom, left: rect.left, width: rect.width })
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [usePortal, isOpen])

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0) {
      const element = document.getElementById(`option-${highlightedIndex}`)
      element?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : selectedOption ? resolvedFormatLabel(selectedOption) : ''}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            if (!isOpen) setIsOpen(true)
            setHighlightedIndex(-1)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={isOpen ? placeholder : placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border rounded-md text-sm dark:bg-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed ${invalid ? 'border-red-400 dark:border-red-600 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
          {value && !isOpen ? (
            <button
              type="button"
              className="pointer-events-auto cursor-pointer inline-flex items-center justify-center"
              onClick={() => {
                onChange('')
                setSearchTerm('')
              }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          ) : (
            <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (() => {
        const listContent = (
          <>
            {onSearch && searching && filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                Searching...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div>
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {!onSearch && options.length === 0 && emptyStateHint ? emptyStateHint : '🔍 No matches found'}
                </div>
                {allowCreate &&
                  (onCreate || onCreateClick) &&
                  searchTerm.trim() && (
                    <button
                      onClick={handleCreate}
                      disabled={isCreating}
                      className="w-full text-left px-3 py-2 text-sm bg-gray-50 dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-500 flex items-center justify-between transition-colors disabled:opacity-50"
                    >
                      <span>
                        + Create{' '}
                        <span className="font-semibold">"{searchTerm.trim()}"</span>
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ⏎
                      </span>
                    </button>
                  )}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.id}
                  id={`option-${index}`}
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    index === highlightedIndex
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                  } ${value === option.id ? 'bg-blue-100 dark:bg-blue-900' : ''}`}
                >
                  {resolvedFormatLabel(option)}
                </button>
              ))
            )}
            {truncatedCount > 0 && (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-600">
                {truncatedCount} more -- keep typing to narrow the list
              </div>
            )}
          </>
        )

        if (usePortal) {
          if (!portalRect) return null
          return createPortal(
            <div
              ref={portalPanelRef}
              style={{ position: 'fixed', top: portalRect.top, left: portalRect.left, width: portalRect.width }}
              className="mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
            >
              {listContent}
            </div>,
            document.body
          )
        }

        return (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
            {listContent}
          </div>
        )
      })()}
    </div>
  )
}
